import { useId, useState } from 'react';
import {
  useCrmTagsQuery, useIntakeSettingsQuery, useMeQuery,
  useSaveIntakeDefaultTagsMutation, useSaveIntakeSettingsMutation,
  type CaptureCommercialStage,
} from './api.js';
import { EMPTY_VOCABULARY } from './CrmTagPicker.js';
import { INTAKE_KINDS, labelFor, parseId, pickerOptions, toSelection, withdrawnNote } from './crmTagSelection.js';
import { Alert, Card, SectionHeading, Spinner } from './ui.js';

/**
 * Settings → Image & File Capture: the defaults for a contact that arrives from a photo, a
 * business card, several photos or a CSV file.
 *
 * Two decisions, and only two. Which ONE CRM tag such a contact starts with, if any. And
 * how phone numbers without a country code are read. Where the contact came from is
 * recorded by the capture itself and shown in Review — it is not a tag, so there is
 * nothing to configure for it here. Nothing on this page changes anything in the CRM.
 */

type Result = { tone: 'success' | 'error'; text: string } | null;

const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900';
const SELECT = 'mt-2 min-h-11 w-full min-w-0 max-w-md rounded-md border border-slate-300 bg-white px-2 text-base disabled:bg-slate-50 sm:text-sm';

const isAdmin = (role: string | null | undefined) => role === 'owner' || role === 'admin';

function errorText(e: unknown, fallback: string): string {
  const status = (e as { status?: number })?.status;
  const data = (e as { data?: { error?: string } })?.data;
  if (status === 403) return 'Only a workspace owner or admin can change this.';
  if (data?.error === 'unknown_tag') return 'That CRM tag is no longer available. Reload and choose again.';
  if (data?.error === 'one_default_tag') return 'Choose one default tag, or none.';
  return fallback;
}

/**
 * The one default CRM tag, as a single select.
 *
 * Saved through PUT /intake/default-tags. The server keeps the stage group single-valued
 * by storing a stage tag and any other tag in two fields; a customer sees one choice, so
 * exactly one of the two is ever sent non-empty. Applied when a contact is first read — a
 * new business card arrives at its check step with it chosen, a CSV row or a photographed
 * list gets it on the server — and never retroactively.
 */
function DefaultTag({ canEdit, onManageTags }: { canEdit: boolean; onManageTags?: () => void }) {
  const settings = useIntakeSettingsQuery();
  const tags = useCrmTagsQuery();
  const [save, { isLoading: saving }] = useSaveIntakeDefaultTagsMutation();
  const [result, setResult] = useState<Result>(null);
  const id = useId();
  const helpId = useId();

  if (settings.isLoading || tags.isLoading) return <Spinner label="Loading the default tag…" />;
  if (!settings.data) return <Alert tone="error">The default tag could not be loaded.</Alert>;

  const vocab = tags.data ?? EMPTY_VOCABULARY;
  const options = pickerOptions(vocab, INTAKE_KINDS);
  const current = toSelection({
    commercialStage: settings.data.defaultCommercialStage ?? null,
    relationshipTypeKeys: [],
    crmTagKeys: settings.data.defaultCrmTagKeys ?? [],
  }, vocab, INTAKE_KINDS)[0] ?? '';
  const known = current === '' || options.some((t) => t.id === current);
  /** The saved default, when it is no longer available: named, and removed by the person. */
  const blocked = current === '' ? null : withdrawnNote([current], vocab);

  const change = async (value: string) => {
    setResult(null);
    const parsed = value === '' ? null : parseId(value);
    try {
      await save({
        defaultCommercialStage: parsed?.kind === 'stage' ? parsed.key as CaptureCommercialStage : null,
        defaultCrmTagKeys: parsed?.kind === 'tag' ? [parsed.key] : [],
      }).unwrap();
      setResult({ tone: 'success', text: 'Saved. Contacts already captured keep their tags.' });
    } catch (e) {
      setResult({ tone: 'error', text: errorText(e, 'The default tag could not be saved.') });
    }
  };

  return (
    <div className="min-w-0 text-sm">
      <label htmlFor={id} className="text-base font-semibold text-slate-900">Default CRM tag</label>
      <p id={helpId} className="mt-1 text-slate-600">
        Applied when a contact is captured from an image or file. You can change it in Review.
      </p>
      {blocked ? (
        <div className="mt-2 min-w-0 space-y-2" data-withdrawn-default="">
          <ul className="flex min-w-0 flex-wrap gap-1.5" aria-label="Current default">
            <li
              className="inline-flex min-h-9 min-w-0 max-w-full items-center gap-1 rounded-full border border-amber-300 bg-amber-50 py-0.5 pl-3 pr-0.5 text-sm text-amber-950"
              data-crm-tag-chip="" data-withdrawn=""
            >
              <span className="min-w-0 [overflow-wrap:anywhere]">
                {labelFor(current, vocab)}<span className="text-xs text-amber-900"> · no longer available</span>
              </span>
              <button
                type="button" disabled={!canEdit || saving} onClick={() => void change('')}
                aria-label={`Remove ${labelFor(current, vocab)}`}
                className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-base leading-none text-amber-900 hover:bg-amber-100 disabled:opacity-50 ${FOCUS}`}
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          </ul>
          <p className="text-sm text-amber-900 [overflow-wrap:anywhere]" data-withdrawn-note="">{blocked}</p>
        </div>
      ) : null}
      <select
        id={id} value={current} aria-describedby={helpId}
        disabled={!canEdit || saving}
        onChange={(e) => void change(e.target.value)}
        className={`${SELECT} ${FOCUS}`}
        data-default-crm-tag=""
      >
        <option value="">No default tag</option>
        {!known ? <option value={current} disabled>{labelFor(current, vocab)} — no longer available</option> : null}
        {options.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>
      {options.length === 0 && onManageTags ? (
        <p className="mt-2 text-slate-600">
          No CRM tags to choose from yet.{' '}
          <button type="button" onClick={onManageTags} className={`inline-flex min-h-11 items-center font-medium text-slate-900 underline ${FOCUS}`}>
            Choose them in CRM Tags
          </button>
        </p>
      ) : null}
      <div aria-live="polite" className="mt-2">{result ? <Alert tone={result.tone}>{result.text}</Alert> : null}</div>
    </div>
  );
}

/**
 * How phone numbers without a country code are read.
 *
 * An explicit administrator choice, never an inference: `(415) 555-0142` is only a US
 * number if somebody says so. The endpoint replaces the whole settings object, so the
 * other stored value is sent back exactly as it was read.
 */
function PhoneRegion({ canEdit }: { canEdit: boolean }) {
  const { data, isLoading } = useIntakeSettingsQuery();
  const [save, { isLoading: saving }] = useSaveIntakeSettingsMutation();
  const [result, setResult] = useState<Result>(null);
  const id = useId();

  if (isLoading) return <Spinner label="Loading…" />;
  if (!data) return <Alert tone="error">The phone number region could not be loaded.</Alert>;

  const change = async (value: string) => {
    setResult(null);
    try {
      await save({
        defaultPhoneRegion: value === '' ? null : value,
        emailLabelingEnabled: data.emailLabelingEnabled,
      }).unwrap();
      setResult({ tone: 'success', text: 'Saved.' });
    } catch (e) {
      setResult({ tone: 'error', text: errorText(e, 'That could not be saved.') });
    }
  };

  return (
    <div className="min-w-0 text-sm">
      <label htmlFor={id} className="font-medium text-slate-900">Phone number region</label>
      <p className="mt-0.5 text-slate-600">
        Used for phone numbers without a country code, from cards, files and emails. Numbers
        that already have one are never changed.
      </p>
      <select
        id={id} value={data.defaultPhoneRegion ?? ''} disabled={!canEdit || saving}
        onChange={(e) => void change(e.target.value)}
        className={`${SELECT} ${FOCUS}`}
      >
        <option value="">Don’t guess — leave them unread</option>
        {data.regions.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
      </select>
      <p className="mt-2 text-xs text-slate-500">
        Affects contacts added afterwards. Contacts already added are left as they were.
      </p>
      <div aria-live="polite" className="mt-2">{result ? <Alert tone={result.tone}>{result.text}</Alert> : null}</div>
    </div>
  );
}

export function ImageFileCapture({ onOpenCrmTags }: {
  onOpenCrmTags?: () => void;
}) {
  const me = useMeQuery();
  const canEdit = isAdmin(me.data?.role);

  return (
    <div className="min-w-0 space-y-6">
      <SectionHeading
        title="Image & File Capture"
        hint="Choose the defaults used when contacts are captured from photos or files. You can change tags for each contact in Review."
      />

      {!canEdit ? (
        <Alert tone="info">Only a workspace owner or admin can change these settings. You can see them here.</Alert>
      ) : null}

      <Card className="p-5">
        <DefaultTag canEdit={canEdit} onManageTags={onOpenCrmTags} />
      </Card>

      <Card className="p-5">
        <h3 className="text-base font-semibold text-slate-900">Phone numbers</h3>
        <div className="mt-3">
          <PhoneRegion canEdit={canEdit} />
        </div>
      </Card>
    </div>
  );
}
